import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { canonicalPatientInstructionHeading, foldPatientInstructionsForDisplay } from '../utils/soap-patient-instructions-format.util';
import {
  flattenSubjectiveStoryLabels,
  canonicalHpiKeepHeading,
  isLikelyShortHeading,
  isMajorSectionBreak,
  matchLabelColon,
  stripMarkdownBold,
  stripMarkdownHorizontalRules
} from '../utils/soap-section-format.util';

/** Controls list vs heading heuristics for AI-generated sections. */
export type SoapToHtmlContext = 'default' | 'hpi' | 'objective' | 'visitSummary' | 'patientInstructions' | 'treatmentPlanSummary' | 'assessmentPlan';

/**
 * Converts SOAP plain text (with • lines from bulletizeNumberedHeadings) into HTML
 * with bold subheadings and bullet lists for content, matching the clinical format.
 */
@Pipe({
  name: 'soapToHtml',
  standalone: true,
  pure: true
})
export class SoapToHtmlPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string | null | undefined, context: SoapToHtmlContext = 'default'): SafeHtml {
    if (!value) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }

    let text = stripMarkdownHorizontalRules(value.replace(/\r/g, ''));
    text = this.preprocessForContext(text, context);
    const html = this.buildHtml(text, context);
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private preprocessForContext(text: string, context: SoapToHtmlContext): string {
    if (context === 'patientInstructions') {
      return foldPatientInstructionsForDisplay(text);
    }
    if (context === 'hpi') {
      text = flattenSubjectiveStoryLabels(text);
    }
    return text;
  }

  private escape(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** Strip **markdown** wrappers for length/word heuristics only. */
  private stripMarkdownBoldRaw(s: string): string {
    return stripMarkdownBold(s);
  }

  /**
   * Escape text but turn paired **segments** into <strong> (safe).
   * Long lines fully wrapped in **...** (common LLM mistake) render as normal body text.
   */
  private escapeWithMarkdownBold(s: string): string {
    if (!s) {
      return '';
    }
    const t = s.trim();
    const fullLineBold = t.match(/^\*\*([^*]+)\*\*$/);
    if (fullLineBold) {
      const inner = fullLineBold[1].trim();
      const wordCount = inner.split(/\s+/).filter(Boolean).length;
      if (wordCount > 4 || inner.length > 48) {
        return this.escape(inner);
      }
    }
    const segments = s.split(/(\*\*[^*]+\*\*)/g);
    return segments
      .map((seg) => {
        const m = seg.match(/^\*\*([^*]+)\*\*$/);
        if (m) {
          return '<strong>' + this.escape(m[1]) + '</strong>';
        }
        return this.escape(seg);
      })
      .join('');
  }

  private isLikelyHeader(line: string): boolean {
    return isLikelyShortHeading(line);
  }

  /** Matches headings like "Problem 4: Sleep disturbance ..." (common in Assessment/Plan). */
  private isProblemHeading(line: string): boolean {
    return /^Problem\s*\d+\s*:/.test(this.stripMarkdownBoldRaw(line));
  }

  /** Matches "Label: content" or "Label: " where label is short (e.g. Onset, Duration, Quality/character). */
  private matchSublabel(line: string): { label: string; content: string } | null {
    return matchLabelColon(line);
  }

  private static readonly ASSESSMENT_SUBSECTION_LABELS = new Set([
    'status',
    'supporting evidence',
    'supporting findings'
  ]);

  private isAssessmentSubsectionLabel(line: string): boolean {
    const t = this.stripMarkdownBoldRaw(line).toLowerCase().replace(/\s*:\s*$/, '').trim();
    return t.length > 0 && t.length <= 50 && !/[.?!]$/.test(t) && SoapToHtmlPipe.ASSESSMENT_SUBSECTION_LABELS.has(t);
  }

  private isObjectiveCategoryHeading(line: string): boolean {
    const t = this.stripMarkdownBoldRaw(line).replace(/\s*:\s*$/, '').trim();
    return /^(physical examination|laboratory,\s*imaging,\s*and diagnostic test results)$/i.test(t);
  }

  /** Problem titles and the Plan: label in the combined Assessment and Plan block. */
  private isAssessmentPlanHeading(line: string, bulletLine: boolean): boolean {
    if (bulletLine) {
      return false;
    }
    const t = this.stripMarkdownBoldRaw(line).trim();
    if (!t) {
      return false;
    }
    if (/^plan:\s*$/i.test(t)) {
      return true;
    }
    if (/[.?!]$/.test(t) || t.length > 160) {
      return false;
    }
    return true;
  }

  private isPlanLabel(line: string): boolean {
    return /^plan:\s*$/i.test(this.stripMarkdownBoldRaw(line).trim());
  }

  private buildPatientInstructionsHtml(text: string): string {
    const lines = text.replace(/(\n\s*){2,}/g, '\n\n').split('\n');
    const parts: string[] = [];
    let inList = false;

    for (const raw of lines) {
      const trimmed = (raw ?? '').trim();
      if (!trimmed) {
        if (inList) {
          parts.push('</ul>');
          inList = false;
        }
        continue;
      }

      const heading = canonicalPatientInstructionHeading(trimmed);
      if (heading) {
        if (inList) {
          parts.push('</ul>');
          inList = false;
        }
        parts.push('<p class="soap-heading">' + this.escape(heading + ':') + '</p>');
        continue;
      }

      const bulletMatch = trimmed.match(/^(?:[-•*]|\d{1,2}\.)\s+(.*)$/);
      if (bulletMatch) {
        const content = (bulletMatch[1] || '').trim();
        if (!inList) {
          parts.push('<ul class="soap-list soap-list--plain">');
          inList = true;
        }
        if (content) {
          parts.push('<li>' + this.escapeWithMarkdownBold(content) + '</li>');
        }
        continue;
      }

      if (inList) {
        parts.push('</ul>');
        inList = false;
      }
      parts.push('<p class="soap-line">' + this.escapeWithMarkdownBold(trimmed) + '</p>');
    }

    if (inList) {
      parts.push('</ul>');
    }
    return parts.join('');
  }

  private buildHtml(text: string, context: SoapToHtmlContext): string {
    if (context === 'patientInstructions') {
      return this.buildPatientInstructionsHtml(text);
    }
    const narrative = context === 'hpi';
    const preferListForBullets =
      context === 'visitSummary' ||
      context === 'treatmentPlanSummary';
    const listClass =
      context === 'treatmentPlanSummary'
        ? 'soap-list soap-list--plain'
        : 'soap-list';

    const normalized = text.replace(/(\n\s*){2,}/g, '\n\n');
    const lines = normalized.split('\n');
    const parts: string[] = [];
    let inList = false;
    let lastWasHeading = false;
    let lastWasProblemHeading = false;
    let lastSubsectionIndex: number | null = null;
    let underHeadingOnlySection = false;
    let inPlanBlock = false;

    for (let i = 0; i < lines.length; i++) {
      const raw = lines[i] ?? '';
      const trimmed = raw.trim();
      const bulletMatch = raw.trimStart().match(/^(?:[-•*]|\d{1,2}\.)\s+(.*)$/);
      const bulletLine = !!bulletMatch;
      const contentAfterBullet = bulletMatch ? (bulletMatch[1] || '').trim() : '';

      if (trimmed === '') {
        if (inList) {
          parts.push('</ul>');
          inList = false;
        }
        underHeadingOnlySection = false;
        lastWasHeading = false;
        lastWasProblemHeading = false;
        lastSubsectionIndex = null;
        inPlanBlock = false;
        continue;
      }

      if (narrative) {
        const textLine = (bulletLine ? contentAfterBullet : trimmed).trim();
        if (!textLine) {
          continue;
        }
        const keepHeading = canonicalHpiKeepHeading(textLine);
        if (keepHeading) {
          if (inList) {
            parts.push('</ul>');
            inList = false;
          }
          parts.push('<p class="soap-heading">' + this.escape(keepHeading) + '</p>');
          continue;
        }
        if (bulletLine && contentAfterBullet) {
          if (!inList) {
            parts.push('<ul class="' + listClass + '">');
            inList = true;
          }
          parts.push('<li>' + this.escapeWithMarkdownBold(contentAfterBullet) + '</li>');
          continue;
        }
        if (inList) {
          parts.push('</ul>');
          inList = false;
        }
        parts.push('<p class="soap-line">' + this.escapeWithMarkdownBold(textLine) + '</p>');
        continue;
      }

      if (underHeadingOnlySection && !isMajorSectionBreak(raw)) {
        const bulletMatchCollect = trimmed.match(/^(?:[-•*]|\d{1,2}\.)\s+(.*)$/);
        const lineBody = bulletMatchCollect ? (bulletMatchCollect[1] || '').trim() : trimmed;
        const subWhileCollecting = this.matchSublabel(lineBody) ?? this.matchSublabel(trimmed);

        if (!inList) {
          parts.push('<ul class="' + listClass + '">');
          inList = true;
        }

        if (subWhileCollecting?.content) {
          parts.push(
            '<li><strong>' +
              this.escape(subWhileCollecting.label) +
              ':</strong> ' +
              this.escapeWithMarkdownBold(subWhileCollecting.content) +
              '</li>'
          );
        } else if (lineBody) {
          parts.push('<li>' + this.escapeWithMarkdownBold(lineBody) + '</li>');
        }
        continue;
      }

      if (lastSubsectionIndex !== null && !bulletLine) {
        const prev = parts[lastSubsectionIndex] || '';
        const prevEndsWithComma = /,<\/p>\s*$/.test(prev);
        const looksLikeYearContinuation = /^\d{4}\b/.test(trimmed);
        if (prevEndsWithComma && looksLikeYearContinuation) {
          parts[lastSubsectionIndex] = prev.replace(/<\/p>\s*$/, ' ' + this.escape(trimmed) + '</p>');
          continue;
        }
      }

      const subFromLine = bulletLine ? this.matchSublabel(contentAfterBullet) : this.matchSublabel(trimmed);
      if (
        subFromLine &&
        !(context === 'objective' && bulletLine) &&
        !(context === 'assessmentPlan' && (inPlanBlock || this.isAssessmentPlanHeading(trimmed, bulletLine)))
      ) {
        if (inList) {
          parts.push('</ul>');
          inList = false;
        }
        const rest = subFromLine.content ? ' ' + this.escape(subFromLine.content) : '';
        lastSubsectionIndex = parts.length;
        parts.push(
          '<p class="soap-subsection"><strong>' +
            this.escape(subFromLine.label) +
            ':</strong>' +
            rest +
            '</p>'
        );
        underHeadingOnlySection = !subFromLine.content;
        lastWasHeading = false;
        lastWasProblemHeading = false;
        continue;
      }
      lastSubsectionIndex = null;
      underHeadingOnlySection = false;

      if (context === 'assessmentPlan' && this.isPlanLabel(trimmed)) {
        if (inList) {
          parts.push('</ul>');
          inList = false;
        }
        parts.push('<p class="soap-heading soap-heading--plan">' + this.escape('Plan:') + '</p>');
        lastWasHeading = true;
        lastWasProblemHeading = false;
        inPlanBlock = true;
        continue;
      }

      if (context === 'assessmentPlan' && inPlanBlock) {
        const title = (bulletLine ? contentAfterBullet : trimmed).trim();
        if (!title) {
          continue;
        }
        if (inList) {
          parts.push('</ul>');
          inList = false;
        }
        if (this.isLikelyHeader(title)) {
          parts.push('<p class="soap-plan-item">' + this.escapeWithMarkdownBold(title) + '</p>');
        } else {
          parts.push('<p class="soap-line">' + this.escapeWithMarkdownBold(title) + '</p>');
        }
        lastWasHeading = false;
        lastWasProblemHeading = false;
        continue;
      }

      if (context === 'assessmentPlan' && this.isAssessmentPlanHeading(trimmed, bulletLine)) {
        if (inList) {
          parts.push('</ul>');
          inList = false;
        }
        parts.push('<p class="soap-heading">' + this.escapeWithMarkdownBold(trimmed) + '</p>');
        lastWasHeading = true;
        lastWasProblemHeading = false;
        inPlanBlock = false;
        continue;
      }

      const asHeader = bulletLine ? contentAfterBullet : trimmed;
      const isProblem = this.isProblemHeading(asHeader);

      if (lastWasProblemHeading && this.isAssessmentSubsectionLabel(asHeader)) {
        let content = '';
        const nextRaw = lines[i + 1] ?? '';
        const nextTrimmed = nextRaw.trim();
        const nextBullet = nextRaw.trimStart().match(/^(?:[-•*]|\d{1,2}\.)\s+(.*)$/);
        if (nextTrimmed && !this.isProblemHeading(nextTrimmed)) {
          content = nextBullet ? (nextBullet[1] || nextTrimmed).trim() : nextTrimmed;
          i += 1;
        }
        if (inList) {
          parts.push('</ul>');
          inList = false;
        }
        const labelOnly = this.stripMarkdownBoldRaw(asHeader).replace(/\s*:\s*$/, '').trim();
        const rest = content ? ' ' + this.escape(content) : '';
        lastSubsectionIndex = parts.length;
        parts.push('<p class="soap-subsection"><strong>' + this.escape(labelOnly) + ':</strong>' + rest + '</p>');
        lastWasHeading = false;
        continue;
      }

      const treatBulletAsListOnly = preferListForBullets && bulletLine;
      const asCategoryHeading =
        context === 'objective'
          ? this.isObjectiveCategoryHeading(asHeader)
          : isProblem || this.isLikelyHeader(asHeader);

      if (
        context === 'objective' &&
        !asCategoryHeading &&
        !bulletLine &&
        this.isLikelyHeader(asHeader)
      ) {
        if (inList) {
          parts.push('</ul>');
          inList = false;
        }
        const labelOnly = this.stripMarkdownBoldRaw(asHeader).replace(/\s*:\s*$/, '').trim();
        parts.push('<p class="soap-subsection"><strong>' + this.escape(labelOnly) + ':</strong></p>');
        lastWasHeading = false;
        lastWasProblemHeading = false;
        underHeadingOnlySection = false;
        continue;
      }

      if (!treatBulletAsListOnly && asCategoryHeading) {
        if (inList) {
          parts.push('</ul>');
          inList = false;
        }
        parts.push('<p class="soap-heading">' + this.escapeWithMarkdownBold(asHeader) + '</p>');
        lastWasHeading = true;
        lastWasProblemHeading = isProblem;
        underHeadingOnlySection = false;
        continue;
      }

      if (bulletLine) {
        const content = (contentAfterBullet || trimmed).trim();
        if (!inList) {
          parts.push('<ul class="' + listClass + '">');
          inList = true;
        }
        if (content) {
          parts.push('<li>' + this.escapeWithMarkdownBold(content) + '</li>');
        }
        lastWasHeading = false;
        underHeadingOnlySection = false;
        continue;
      }

      if (inList) {
        parts.push('</ul>');
        inList = false;
      }
      parts.push('<p class="soap-line">' + this.escapeWithMarkdownBold(trimmed) + '</p>');
      lastWasHeading = false;
      underHeadingOnlySection = false;
    }

    if (inList) {
      parts.push('</ul>');
    }

    return parts.join('');
  }
}
